import {
    SquareOnlineStoreBriefFields_inspirationFiles,
    SquareOnlineStoreBriefFields_referenceFiles,
} from '__generated__/SquareOnlineStoreBriefFields';
import { SquareOnlineStoreExistingAssets, SquareOnlineStoreProjectType } from '__generated__/types';
import { Uploader } from '__generated__/Uploader';
import CheckboxList from 'common/components/CheckboxList';
import { Currency } from 'common/utils/currency';
import BoxSelectField from 'projects/components/BriefFields/BoxSelectField';
import DescriptionField from 'projects/components/BriefFields/DescriptionField';
import InspirationFilesField from 'projects/components/BriefFields/InspirationFilesField';
import InspirationUrlsField from 'projects/components/BriefFields/InspirationUrlsField';
import LanguageField from 'projects/components/BriefFields/LanguageField';
import ReferenceFilesField from 'projects/components/BriefFields/ReferenceFilesField';
import TitleField from 'projects/components/BriefFields/TitleField';
import PriceGuide from 'projects/components/BudgetSlider/PriceGuide';
import ContentSetting from 'projects/components/ContentSetting';
import { ObservableField as Field, ObservableForm } from 'projects/components/ObservableForm';
import { trackViewedBriefEvent } from 'projects/events';
import React, { useEffect } from 'react';
import { projectTypes } from './projectTypes';

interface Fields {
    language: string;
    title: string;
    description: string;
    projectType: SquareOnlineStoreProjectType;
    referenceFiles: SquareOnlineStoreBriefFields_referenceFiles[];
    inspirationFiles: SquareOnlineStoreBriefFields_inspirationFiles[];
    inspirationUrls: string[];
    existingAssets: SquareOnlineStoreExistingAssets[];
}

export interface Props<T> {
    form: ObservableForm<T>;
    brief: {
        id: string;
        uploader: Uploader;
    };
}

const DetailsPage: <T extends Fields>(props: Props<T>) => React.ReactElement<Props<T>> = ({
    form,
    brief,
}) => {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Details',
        });
    }, []);

    return (
        <ContentSetting>
            <LanguageField form={form} />
            <TitleField
                form={form}
                placeholderText={__('E.g. Hip Florist needs fresh design for Square Online Store')}
            />

            <BoxSelectField
                form={form}
                name="projectType"
                title={__('What best describes your need?')}
                required
                options={Object.keys(projectTypes).map((key: SquareOnlineStoreProjectType) => ({
                    value: key,
                    title: projectTypes[key].title,
                    label: <PriceGuide min={projectTypes[key].price} currency={Currency.USD} />,
                }))}
            />

            <DescriptionField
                form={form}
                placeholderText={__(
                    "E.g. I'm a florist and my shop is called Petal to the Metal. I'm trying to get my site up and going using Square Online Store and I need some help. I have about 40 different products that I sell and even offer delivery. I've put a lot of care into my brand and I like my site to be clean and easy to use while still conveying my unique brand."
                )}
            />
            <ReferenceFilesField form={form} uploader={brief.uploader} />
            <InspirationFilesField form={form} uploader={brief.uploader} />
            <InspirationUrlsField form={form} />

            <Field
                form={form}
                name="existingAssets"
                title={__('Do you have any of the following?')}
                render={() => (
                    <CheckboxList
                        options={[
                            {
                                value: SquareOnlineStoreExistingAssets.LOGO_AND_BRANDING,
                                label: __('Logo & Branding'),
                            },
                            {
                                value: SquareOnlineStoreExistingAssets.IMAGES_AND_VISUAL_ASSETS,
                                label: __('Images & Visual Assets'),
                            },
                            {
                                value: SquareOnlineStoreExistingAssets.WEBSITE_AND_PRODUCT_COPY,
                                label: __('Website & Product Copy'),
                            },
                        ]}
                        {...form.attach('existingAssets')}
                    />
                )}
            />
        </ContentSetting>
    );
};

export default DetailsPage;
